<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useStore } from '@/stores/main'

const route = useRoute()
const store = useStore()

const navItems = [
  { path: '/', icon: 'i-lucide:layout-dashboard', label: 'Dashboard' },
  { path: '/projects', icon: 'i-lucide:folder', label: 'Projects' },
  { path: '/loop', icon: 'i-lucide:repeat', label: 'Loop Control' },
  { path: '/events', icon: 'i-lucide:activity', label: 'Events' },
  { path: '/settings', icon: 'i-lucide:settings', label: 'Settings' }
]

const isActive = (path: string) => route.path === path

const healthStatus = computed(() => store.health?.status || 'unknown')
</script>

<template>
  <aside class="sidebar">
    <div class="sidebar-header">
      <div class="logo">
        <span class="i-lucide:code-2 text-2xl"></span>
        <span class="logo-text">UnLoveable</span>
      </div>
      <div class="health-indicator" :class="healthStatus">
        <span class="i-lucide:circle text-xs"></span>
        <span>{{ healthStatus }}</span>
      </div>
    </div>

    <nav class="sidebar-nav">
      <router-link
        v-for="item in navItems"
        :key="item.path"
        :to="item.path"
        class="nav-item"
        :class="{ active: isActive(item.path) }"
      >
        <span :class="item.icon"></span>
        <span>{{ item.label }}</span>
      </router-link>
    </nav>

    <div class="sidebar-footer">
      <div class="version">
        <span class="i-lucide:git-branch text-sm"></span>
        <span>v1.0.0</span>
      </div>
    </div>
  </aside>
</template>

<style scoped>
.sidebar {
  width: 260px;
  background: var(--bg-secondary);
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--border);
}

.logo {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.125rem;
}

.logo-text {
  background: linear-gradient(135deg, var(--accent), #a855f7);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.health-indicator {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
  font-size: 0.75rem;
  color: var(--text-muted);
}

.health-indicator.ok {
  color: var(--success);
}

.health-indicator.error {
  color: var(--error);
}

.sidebar-nav {
  flex: 1;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  color: var(--text-secondary);
  text-decoration: none;
  transition: all 0.15s ease;
}

.nav-item:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
  text-decoration: none;
}

.nav-item.active {
  background: var(--accent);
  color: white;
}

.sidebar-footer {
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
}

.version {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--text-muted);
}
</style>
